/**
 * Enhanced input component with built-in validation
 */

import React, { useRef, useCallback, useEffect } from 'react'
import type { FieldValidationConfig, ValidationState } from '../types'
import { useFieldValidation } from '../hooks/useFieldValidation'

interface ValidatedInputProps {
  name: string
  label?: string
  value: string
  onChange: (name: string, value: string) => void
  onValidation?: (name: string, isValid: boolean, message?: string) => void
  validationConfig?: FieldValidationConfig
  validationState?: ValidationState[string]
  type?: 'text' | 'email' | 'tel' | 'password' | 'url' | 'number' | 'date'
  placeholder?: string
  helpText?: string
  required?: boolean
  disabled?: boolean
  className?: string
  autoComplete?: string
  // Additional number input properties
  step?: string
  min?: string
  max?: string
}

export const ValidatedInput: React.FC<ValidatedInputProps> = ({
  name,
  label,
  value,
  onChange,
  onValidation,
  validationConfig = {},
  validationState: externalValidationState,
  type = 'text',
  placeholder,
  helpText,
  required,
  disabled,
  className = '',
  autoComplete,
  step,
  min,
  max
}) => {
  const { validationState: internalValidationState, validateField, formatInput } = useFieldValidation({
    required,
    ...validationConfig
  })

  // Use external validation state if provided, otherwise use internal
  const validationState = externalValidationState || internalValidationState

  // Debug logging for ownership percentage
  if (name === 'contactOwnershipPercentage') {
    console.log(`🔧 ValidatedInput ${name} state selection:`)
    console.log(`   External state:`, externalValidationState)
    console.log(`   Internal state:`, internalValidationState)
    console.log(`   Using state:`, validationState)
  }

  const lastValueRef = useRef<string>(value)
  const lastCursorPositionRef = useRef<number>(0)

  // Update ref when value prop changes
  useEffect(() => {
    lastValueRef.current = value
  }, [value])

  // Remove the problematic useEffect that was causing infinite loops
  // Validation will happen on user interaction (change/blur) instead of on mount

  // Smart formatting functions that handle deletions properly for different field types
  const smartPhoneFormat = useCallback((newValue: string, oldValue: string, cursorPosition: number): string => {
    const newDigits = newValue.replace(/\D/g, '')
    const oldDigits = oldValue.replace(/\D/g, '')
    
    // If user deleted digits (not just formatting), use the regular formatter
    if (newDigits.length < oldDigits.length) {
      // User is deleting - just format what remains
      if (newDigits.length === 0) return ''
      if (newDigits.length <= 3) return `(${newDigits}`
      if (newDigits.length <= 6) return `(${newDigits.slice(0, 3)}) ${newDigits.slice(3)}`
      return `(${newDigits.slice(0, 3)}) ${newDigits.slice(3, 6)}-${newDigits.slice(6, 10)}`
    }
    
    // User is adding - use regular formatter
    return formatInput(newValue)
  }, [formatInput])

  // Smart SSN formatting that handles deletions properly
  const smartSSNFormat = useCallback((newValue: string, oldValue: string, cursorPosition: number): string => {
    const newDigits = newValue.replace(/\D/g, '')
    const oldDigits = oldValue.replace(/\D/g, '')
    
    // If user deleted digits (not just formatting), use the regular formatter
    if (newDigits.length < oldDigits.length) {
      // User is deleting - just format what remains
      if (newDigits.length === 0) return ''
      if (newDigits.length <= 3) return newDigits
      if (newDigits.length <= 5) return `${newDigits.slice(0, 3)}-${newDigits.slice(3)}`
      return `${newDigits.slice(0, 3)}-${newDigits.slice(3, 5)}-${newDigits.slice(5, 9)}`
    }
    
    // User is adding - use regular formatter
    return formatInput(newValue)
  }, [formatInput])

  // Smart Federal Tax ID formatting that handles deletions properly
  const smartFedTaxIdFormat = useCallback((newValue: string, oldValue: string, cursorPosition: number): string => {
    const newDigits = newValue.replace(/\D/g, '')
    const oldDigits = oldValue.replace(/\D/g, '')
    
    // If user deleted digits (not just formatting), use the regular formatter
    if (newDigits.length < oldDigits.length) {
      // User is deleting - just format what remains
      if (newDigits.length === 0) return ''
      if (newDigits.length <= 2) return newDigits
      return `${newDigits.slice(0, 2)}-${newDigits.slice(2, 9)}`
    }
    
    // User is adding - use regular formatter
    return formatInput(newValue)
  }, [formatInput])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value
    const oldValue = lastValueRef.current
    const cursorPosition = e.target.selectionStart || 0
    
    // Auto-format if enabled
    if (validationConfig.formatOnInput) {
      // Check field type for smart formatting
      const isPhoneField = validationConfig.validators?.some(v => v.name === 'phoneNumber')
      const isSSNField = validationConfig.validators?.some(v => v.name === 'ssnWithDashes')
      const isFedTaxIdField = validationConfig.validators?.some(v => v.name === 'fedTaxIdWithDashes')
      
      if (isPhoneField) {
        newValue = smartPhoneFormat(newValue, oldValue, cursorPosition)
      } else if (isSSNField) {
        newValue = smartSSNFormat(newValue, oldValue, cursorPosition)
      } else if (isFedTaxIdField) {
        newValue = smartFedTaxIdFormat(newValue, oldValue, cursorPosition)
      } else {
        newValue = formatInput(newValue)
      }
    }
    
    // Update refs
    lastValueRef.current = newValue
    lastCursorPositionRef.current = cursorPosition
    
    onChange(name, newValue)
    
    // Validate on change if configured or if field was already validated
    if (validationConfig.validateOnChange || validationState.isValidated) {
      const result = validateField(newValue)
      onValidation?.(name, result.isValid, result.message)
    }
  }

  const handleBlur = () => {
    // Always validate on blur (default behavior)
    if (validationConfig.validateOnBlur !== false) {
      const result = validateField(value)
      onValidation?.(name, result.isValid, result.message)
    }
  }

  const getInputClassName = () => {
    let inputClass = `validated-input ${className}`
    
    if (validationState.isValidated) {
      inputClass += validationState.isValid ? ' valid' : ' invalid'
    }
    
      // Debug logging for ownership percentage
  if (name === 'contactOwnershipPercentage') {
    console.log(`🎨 UI rendering for ${name}:`)
    console.log(`   Value: "${value}"`)
    console.log(`   Validation state:`, validationState)
    console.log(`   CSS class: "${inputClass}"`)
    console.log(`   Should show error:`, validationState.isValidated && validationState.message)
    console.log(`   Component will render error:`, validationState.isValidated && validationState.message)
  }
    
    return inputClass
  }

  const showValidationIcon = validationConfig.showSuccessIcon !== false && validationState.isValidated

  return (
    <div className="validated-input-wrapper">
      {label && (
        <label htmlFor={name} className="validated-input-label">
          {label}
          {required && <span className="required-indicator"> *</span>}
        </label>
      )}
      
      <div className="validated-input-container">
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          step={step}
          min={min}
          max={max}
          className={getInputClassName()}
          aria-invalid={validationState.isValidated && !validationState.isValid}
          aria-describedby={
            (validationState.message && `${name}-error`) || 
            (helpText && `${name}-help`) || 
            undefined
          }
        />
        
        {showValidationIcon && (
          <div className="validation-icon">
            {validationState.isValid ? (
              <i className="bi bi-check-circle text-success" aria-hidden="true"></i>
            ) : (
              <i className="bi bi-exclamation-circle text-danger" aria-hidden="true"></i>
            )}
          </div>
        )}
      </div>
      
      {validationState.isValidated && validationState.message && (
        <div 
          id={`${name}-error`}
          className="validation-message error"
          role="alert"
          aria-live="polite"
        >
          <i className="bi bi-exclamation-triangle me-1" aria-hidden="true"></i>
          {validationState.message}
        </div>
      )}
      
      {helpText && !validationState.message && (
        <div id={`${name}-help`} className="help-text">
          {helpText}
        </div>
      )}
    </div>
  )
}
